import { useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { getLessonById } from "@/services/lessonService";
import { 
  addContentItem,
  updateContentItem,
  deleteContentItem,
  getContentItemsByLessonId,
  reorderContentItems
} from "@/services/contentManagement.service";
import { ContentItem, Lesson } from "@/types/course";

export interface ContentItemForm {
  id?: string;
  title: string;
  description: string;
  content_type: string;
  url: string;
  file_path: string;
  is_required: boolean;
  duration: string;
}

export const useLessonContent = (lessonId: string | undefined) => {
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<string | null>(null);
  const [currentContentItem, setCurrentContentItem] = useState<ContentItemForm>({
    title: "",
    description: "",
    content_type: "video",
    url: "",
    file_path: "",
    is_required: false,
    duration: "",
  });

  const fetchLessonAndContent = async () => {
    setIsLoading(true);
    try {
      if (!lessonId) return;
      
      // Fetch lesson data
      const lessonData = await getLessonById(lessonId);
      setLesson(lessonData);
      
      // Fetch content items for this lesson
      const contentData = await getContentItemsByLessonId(lessonId);
      setContentItems(contentData);
    } catch (error) {
      console.error("Error fetching lesson data:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch lesson data. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateContent = async () => {
    try {
      if (!lessonId || !currentContentItem.title.trim() || !currentContentItem.content_type) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Title and content type are required.",
        });
        return;
      }

      const newOrderIndex = contentItems.length > 0 
        ? Math.max(...contentItems.map(item => item.order_index || 0)) + 1 
        : 0;
      
      const durationValue = currentContentItem.duration 
        ? parseInt(currentContentItem.duration, 10) 
        : null;
      
      await addContentItem(
        lessonId,
        currentContentItem.title,
        currentContentItem.description || null,
        currentContentItem.content_type,
        currentContentItem.url || null,
        currentContentItem.file_path || null,
        currentContentItem.is_required,
        newOrderIndex,
        durationValue
      );
      
      toast({
        title: "Success",
        description: "Content item created successfully.",
      });
      
      setIsCreating(false);
      resetContentForm();
      fetchLessonAndContent();
    } catch (error) {
      console.error("Error creating content item:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create content item. Please try again.",
      });
    }
  };

  const handleEditContent = async () => {
    try {
      if (!currentContentItem.id || !currentContentItem.title.trim() || !currentContentItem.content_type) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Title and content type are required.",
        });
        return;
      }

      const durationValue = currentContentItem.duration 
        ? parseInt(currentContentItem.duration, 10) 
        : null;
      
      await updateContentItem(currentContentItem.id, {
        title: currentContentItem.title,
        description: currentContentItem.description || null,
        content_type: currentContentItem.content_type,
        url: currentContentItem.url || null,
        file_path: currentContentItem.file_path || null,
        is_required: currentContentItem.is_required,
        duration: durationValue
      });
      
      toast({
        title: "Success",
        description: "Content item updated successfully.",
      });
      
      setIsEditing(false);
      resetContentForm();
      fetchLessonAndContent();
    } catch (error) {
      console.error("Error updating content item:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update content item. Please try again.",
      });
    }
  };

  const handleDeleteContent = async () => {
    try {
      if (!contentToDelete) return;

      await deleteContentItem(contentToDelete);
      
      toast({
        title: "Success",
        description: "Content item deleted successfully.",
      });
      
      setIsDeleteDialogOpen(false);
      setContentToDelete(null);
      fetchLessonAndContent();
    } catch (error) {
      console.error("Error deleting content item:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete content item. Please try again.",
      });
    }
  };

  const handleMoveContent = async (contentId: string, direction: "up" | "down") => {
    const contentIndex = contentItems.findIndex(item => item.id === contentId);
    
    if (
      (direction === "up" && contentIndex === 0) || 
      (direction === "down" && contentIndex === contentItems.length - 1)
    ) {
      return;
    }

    const newContentItems = [...contentItems];
    const targetIndex = direction === "up" ? contentIndex - 1 : contentIndex + 1;
    
    // Swap items
    [newContentItems[contentIndex], newContentItems[targetIndex]] = [
      newContentItems[targetIndex],
      newContentItems[contentIndex],
    ];
    
    try {
      if (!lessonId) return;
      await reorderContentItems(lessonId, newContentItems.map(item => item.id));
      
      // Update local state
      setContentItems(newContentItems);
      
      toast({
        title: "Success",
        description: "Content order updated.",
      });
    } catch (error) {
      console.error("Error reordering content items:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to reorder content items. Please try again.",
      });
    }
  };

  const resetContentForm = () => {
    setCurrentContentItem({
      title: "",
      description: "",
      content_type: "video",
      url: "",
      file_path: "",
      is_required: false,
      duration: "",
    });
  };

  const openEditDialog = (item: ContentItem) => {
    setCurrentContentItem({
      id: item.id,
      title: item.title,
      description: item.description || "",
      content_type: item.content_type || "",
      url: item.url || "",
      file_path: item.file_path || "",
      is_required: item.is_required || false,
      duration: item.duration ? item.duration.toString() : "",
    });
    setIsEditing(true);
  };

  const openDeleteDialog = (id: string) => {
    setContentToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetContentForm();
    setIsCreating(true);
  };

  useEffect(() => {
    if (lessonId) {
      fetchLessonAndContent();
    }
  }, [lessonId]);

  return {
    lesson,
    contentItems,
    isLoading,
    isCreating,
    isEditing,
    isDeleteDialogOpen,
    currentContentItem,
    contentToDelete,
    setCurrentContentItem,
    setIsCreating,
    setIsEditing,
    setIsDeleteDialogOpen,
    handleCreateContent,
    handleEditContent,
    handleDeleteContent,
    handleMoveContent,
    openEditDialog,
    openDeleteDialog,
    openCreateDialog,
  };
};
